import { apiFetch } from './auth';
import type { ChatSessionConfigSnapshot, ContextKernelV1 } from '../types';
import type { EngineeringSpecV1 } from '@realplc/types';

const API_BASE = import.meta.env.VITE_API_URL || '';

export interface ProjectRecord {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  vendorType?: string;
  visibility: string;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

export interface ProjectSessionSummary {
  id: string;
  projectId?: string;
  userId: string;
  title: string;
  status?: string;
  sessionConfig?: ChatSessionConfigSnapshot;
  isStreaming: boolean;
  isStarred: boolean;
  artifactCount: number;
  updatedAt: number;
}

export interface ProjectArtifactSummary {
  id: string;
  projectId: string;
  organizationId: string;
  name: string;
  type: string;
  status: string;
  version: number;
  validationStatus?: string;
  updatedAt: number;
  content?: string;
}

export interface ProjectWorkspaceSnapshot {
  projectId: string;
  defaultVendor?: string;
  activeArtifactId?: string;
  latestPublishedArtifactId?: string;
  factSnapshot?: unknown;
  contextKernel?: ContextKernelV1;
}

export async function loadProjects(): Promise<ProjectRecord[]> {
  const response = await apiFetch(`${API_BASE}/api/projects`);
  if (!response.ok) throw new Error('Failed to load projects');
  return response.json() as Promise<ProjectRecord[]>;
}

export async function createProject(name: string): Promise<ProjectRecord> {
  const response = await apiFetch(`${API_BASE}/api/projects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({})) as any;
    throw new Error(error.message || 'Failed to create project');
  }
  return response.json() as Promise<ProjectRecord>;
}

export async function updateProject(id: string, data: { name?: string }): Promise<ProjectRecord> {
  const response = await apiFetch(`${API_BASE}/api/projects/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to update project');
  return response.json() as Promise<ProjectRecord>;
}

export async function deleteProject(id: string): Promise<void> {
  const response = await apiFetch(`${API_BASE}/api/projects/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to delete project');
}

export interface ProjectWorkspace {
  project: ProjectRecord;
  sessions: ProjectSessionSummary[];
  artifacts?: ProjectArtifactSummary[];
  activeArtifact?: ProjectArtifactSummary | null;
  latestValidatedArtifact?: ProjectArtifactSummary | null;
  latestContextKernel?: ContextKernelV1;
  projectSnapshot?: ProjectWorkspaceSnapshot;
}

/**
 * The project's current engineering spec, or null when it has none yet.
 *
 * Separate from the workspace call on purpose: that one is made for every
 * project when sessions are restored, and this is a document a single panel
 * reads.
 */
export async function loadProjectSpec(projectId: string): Promise<EngineeringSpecV1 | null> {
  const response = await apiFetch(`${API_BASE}/api/projects/${encodeURIComponent(projectId)}/spec`);
  if (!response.ok) throw new Error('Failed to load project spec');
  const body = await response.json() as { spec?: EngineeringSpecV1 | null };
  return body.spec ?? null;
}

export async function loadProjectWorkspace(projectId: string): Promise<ProjectWorkspace> {
  const response = await apiFetch(
    `${API_BASE}/api/projects/${encodeURIComponent(projectId)}/workspace?sessionLimit=100`,
  );
  if (!response.ok) throw new Error('Failed to load project workspace');
  return response.json() as Promise<ProjectWorkspace>;
}
